import React, { FunctionComponent, forwardRef, useMemo, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Node } from "@iventis/domain-model/model/node";
import { StyledInteractiveArea, skeletonShimmer, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { CursorTooltip } from "@iventis/components";
import { NodeWithHierarchy } from "@iventis/tree-browser";
import { Content } from "@iventis/translations";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { toast } from "@iventis/toasts/src/toast";
import { StyledHeader } from "./library-tile-node";
import { StyledLibraryTileNode } from "./library-tile-node.styles";
import { FavouriteButtonComponent } from "./library-nodes";
import { favouriteNodeServices } from "../services/library-api-functions";
import { BottomUpBreadcrumbComponent } from "./bottom-up-breadcrumb";

export interface BasicTileNodeProps {
    node: NodeWithHierarchy;
    preview: React.ReactNode;
    content: React.ReactNode;
    updateNode: (updatedNode: Node) => void;
    open: (event, node) => void;
}

export const BasicTileNode: FunctionComponent<BasicTileNodeProps> = ({ node, preview, content, updateNode, open }) => {
    const theme = useTheme<Theme>();

    const setFavourite = (value) => {
        if (value) {
            favouriteNodeServices.addFavourite(node.id);
        } else {
            favouriteNodeServices.deleteFavourite(node.id);
        }
        updateNode({ ...node, favourite: value });
    };

    const nameContainerRef = useRef<HTMLDivElement>();
    const textInOverflow = useMemo(() => nameContainerRef.current?.offsetHeight < nameContainerRef.current?.scrollHeight, [node, nameContainerRef.current]);

    return (
        <IventisErrorBoundaryWithToast toast={toast}>
            <StyledLibraryTileNode
                singleTopRowButton
                fontWeight={400}
                borderColour={theme.shades.three}
                selected={false}
                emptyNode={false}
                onClick={(event) => open(event, node)}
                onDoubleClick={(event) => open(event, node)}
                isFavourite={node.favourite}
                aria-selected={false}
                draggingNode={false}
                className="card"
            >
                <div className="gallery-card-top-row">
                    <FavouriteButtonComponent checked={node.favourite} onChange={setFavourite} className="favourite" />
                </div>
                <div className="gallery-card-main">
                    <div className="gallery-thumbnail">
                        {preview}
                        <StyledInteractiveArea onClick={(event) => open(event, node)} />
                    </div>
                    <div className="content">
                        <StyledHeader ref={nameContainerRef}>{node.name}</StyledHeader>
                        <CursorTooltip text={node.name} disabled={!textInOverflow}>
                            <StyledInteractiveArea onClick={(event) => open(event, node)} data-cy={`open-node-${node.id}`} />
                        </CursorTooltip>
                        <div className="breadcrumbs-container">
                            <BottomUpBreadcrumbComponent bottomUpHierarchy={node.bottomUpHierarchy} endingParents={1} translatableContent={Content.library2.mappingLibraries} />
                        </div>
                        {content}
                    </div>
                </div>
            </StyledLibraryTileNode>
        </IventisErrorBoundaryWithToast>
    );
};

export const BasicSkeletonTileNode = forwardRef<HTMLDivElement, { sequenceNumber: number; totalElementCount: number; includeBreadcrumb?: boolean }>(
    ({ sequenceNumber, totalElementCount, includeBreadcrumb = false }, ref) => {
        const theme = useTheme<Theme>();

        return (
            <StyledLibraryTileNode
                singleTopRowButton
                fontWeight={400}
                borderColour={theme.shades.three}
                selected={false}
                emptyNode
                aria-selected={false}
                draggingNode={false}
                className="card"
                style={{ opacity: 1 - sequenceNumber / totalElementCount }}
                ref={ref}
            >
                <div className="gallery-card-top-row" />
                <div className="gallery-card-main">
                    <div className="gallery-thumbnail">
                        <SkeletonImage />
                    </div>
                    <div className="content">
                        <StyledHeader>
                            <div style={{ width: "100%", height: "50%" }}>
                                <SkeletonPlaceholder />
                            </div>
                            <div style={{ width: "80%", height: "50%" }}>
                                <SkeletonPlaceholder />
                            </div>
                        </StyledHeader>
                        {includeBreadcrumb && (
                            <div className="breadcrumbs-container">
                                <div style={{ width: "90%", height: "50%" }}>
                                    <SkeletonPlaceholder />
                                </div>
                            </div>
                        )}
                        <div className="gallery-node-content">
                            <div className="colour-on-focus" style={{ width: "70%", height: "1em" }}>
                                <SkeletonPlaceholder />
                            </div>
                            <div className="colour-on-focus" style={{ width: "90%", height: "1em" }}>
                                <SkeletonPlaceholder />
                            </div>
                        </div>
                    </div>
                </div>
            </StyledLibraryTileNode>
        );
    }
);

const SkeletonPlaceholder = styled.span`
    ${skeletonShimmer({ sequence: 0 })};
    transform: scale(1, 0.9);
    overflow: hidden;
    width: 100%;
    height: 100%;
`;

const SkeletonImage = styled.span`
    ${skeletonShimmer({ sequence: 0 })};
    width: 100%;
    height: 100%;
`;
